"use client";

import { c as _c } from "react/compiler-runtime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useId, useState } from "react";
import type { ComponentPropsWithoutRef, FocusEvent, MouseEvent } from "react";
interface TooltipIconButtonProps extends Omit<ComponentPropsWithoutRef<"button">, "children"> {
  readonly icon: IconDefinition;
  readonly tooltipText: string;
  readonly iconClassName?: string;
  readonly tooltipWidth?: string;
  readonly tooltipPosition?: "top" | "bottom" | "left" | "right";
}
export default function TooltipIconButton(t0) {
  const $ = _c(53);
  let ariaDescribedByProp;
  let className;
  let icon;
  let onBlur;
  let onClick;
  let onFocus;
  let onMouseEnter;
  let onMouseLeave;
  let rest;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let tooltipText;
  if ($[0] !== t0) {
    ({
      icon,
      tooltipText,
      iconClassName: t1,
      tooltipWidth: t2,
      tooltipPosition: t3,
      type: t4,
      tabIndex: t5,
      className,
      onClick,
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseLeave,
      "aria-describedby": ariaDescribedByProp,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = ariaDescribedByProp;
    $[2] = className;
    $[3] = icon;
    $[4] = onBlur;
    $[5] = onClick;
    $[6] = onFocus;
    $[7] = onMouseEnter;
    $[8] = onMouseLeave;
    $[9] = rest;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = tooltipText;
  } else {
    ariaDescribedByProp = $[1];
    className = $[2];
    icon = $[3];
    onBlur = $[4];
    onClick = $[5];
    onFocus = $[6];
    onMouseEnter = $[7];
    onMouseLeave = $[8];
    rest = $[9];
    t1 = $[10];
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    tooltipText = $[15];
  }
  const iconClassName = t1 === undefined ? "tw-text-iron-400 tw-size-4" : t1;
  const tooltipWidth = t2 === undefined ? "tw-w-72" : t2;
  const tooltipPosition = t3 === undefined ? "top" : t3;
  const type = t4 === undefined ? "button" : t4;
  const tabIndex = t5 === undefined ? 0 : t5;
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const tooltipId = useId();
  const tooltipElementId = `${tooltipId}-tooltip`;
  let t6;
  if ($[16] !== ariaDescribedByProp || $[17] !== tooltipElementId) {
    t6 = [ariaDescribedByProp, tooltipElementId].filter(Boolean);
    $[16] = ariaDescribedByProp;
    $[17] = tooltipElementId;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const ariaDescribedBy = t6.join(" ");
  const buttonClassName = className ? `tw-relative tw-cursor-pointer tw-bg-transparent tw-border-none tw-p-0 ${className}` : "tw-relative tw-cursor-pointer tw-bg-transparent tw-border-none tw-p-0";
  const showTooltip = isHovered || isFocused;
  let t7;
  if ($[19] !== tooltipPosition) {
    t7 = () => {
      switch (tooltipPosition) {
        case "top":
          {
            return "tw-bottom-6 tw-left-1/2 -tw-translate-x-1/2";
          }
        case "bottom":
          {
            return "tw-top-6 tw-left-1/2 -tw-translate-x-1/2";
          }
        case "left":
          {
            return "tw-right-6 tw-top-1/2 -tw-translate-y-1/2";
          }
        case "right":
          {
            return "tw-left-6 tw-top-1/2 -tw-translate-y-1/2";
          }
      }
    };
    $[19] = tooltipPosition;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const getPositionClasses = t7;
  let t8;
  if ($[21] !== onMouseEnter) {
    t8 = event => {
      setIsHovered(true);
      onMouseEnter?.(event);
    };
    $[21] = onMouseEnter;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const handleMouseEnter = t8;
  let t9;
  if ($[23] !== onMouseLeave) {
    t9 = event_0 => {
      setIsHovered(false);
      onMouseLeave?.(event_0);
    };
    $[23] = onMouseLeave;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const handleMouseLeave = t9;
  let t10;
  if ($[25] !== onFocus) {
    t10 = event_1 => {
      setIsFocused(true);
      onFocus?.(event_1);
    };
    $[25] = onFocus;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const handleFocus = t10;
  let t11;
  if ($[27] !== onBlur) {
    t11 = event_2 => {
      setIsFocused(false);
      onBlur?.(event_2);
    };
    $[27] = onBlur;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const handleBlur = t11;
  let t12;
  if ($[29] !== onClick) {
    t12 = event_3 => {
      event_3.stopPropagation();
      onClick?.(event_3);
    };
    $[29] = onClick;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const handleClick = t12;
  let t13;
  if ($[31] !== icon || $[32] !== iconClassName) {
    t13 = <FontAwesomeIcon icon={icon} className={iconClassName} />;
    $[31] = icon;
    $[32] = iconClassName;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== getPositionClasses || $[35] !== showTooltip || $[36] !== tooltipElementId || $[37] !== tooltipText || $[38] !== tooltipWidth) {
    t14 = showTooltip && <div id={tooltipElementId} role="tooltip" className={`tw-absolute ${getPositionClasses()} ${tooltipWidth} tw-p-3 tw-bg-iron-900 tw-text-iron-100 tw-text-xs tw-rounded-lg tw-shadow-lg tw-z-10`}>{tooltipText}</div>;
    $[34] = getPositionClasses;
    $[35] = showTooltip;
    $[36] = tooltipElementId;
    $[37] = tooltipText;
    $[38] = tooltipWidth;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== ariaDescribedBy || $[41] !== buttonClassName || $[42] !== handleBlur || $[43] !== handleClick || $[44] !== handleFocus || $[45] !== handleMouseEnter || $[46] !== handleMouseLeave || $[47] !== rest || $[48] !== t13 || $[49] !== t14 || $[50] !== tabIndex || $[51] !== type) {
    t15 = <button type={type} className={buttonClassName} tabIndex={tabIndex} aria-describedby={ariaDescribedBy} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onFocus={handleFocus} onBlur={handleBlur} onClick={handleClick} {...rest}>{t13}{t14}</button>;
    $[40] = ariaDescribedBy;
    $[41] = buttonClassName;
    $[42] = handleBlur;
    $[43] = handleClick;
    $[44] = handleFocus;
    $[45] = handleMouseEnter;
    $[46] = handleMouseLeave;
    $[47] = rest;
    $[48] = t13;
    $[49] = t14;
    $[50] = tabIndex;
    $[51] = type;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  return t15;
}